import { type MutableRefObject, useEffect } from 'react'

const EDGE_BAND = 84
const EDGE_SPEED = 18

export interface PointerSpot {
  x: number
  y: number
}

const pushAlong = (spot: number, start: number, end: number) => {
  const before = start + EDGE_BAND - spot
  const after = spot - (end - EDGE_BAND)
  if (before > 0) return -Math.min(before / EDGE_BAND, 1) * EDGE_SPEED
  if (after > 0) return Math.min(after / EDGE_BAND, 1) * EDGE_SPEED
  return 0
}

export const holdsPointer = (container: HTMLElement, spot: PointerSpot) => {
  const box = container.getBoundingClientRect()
  return (
    spot.x >= box.left &&
    spot.x <= box.right &&
    spot.y >= box.top &&
    spot.y <= box.bottom
  )
}

// Both axes, because the strip runs across and the map runs down, and one drag can reach the
// edge of either. A pane that cannot scroll an axis simply ignores its push.
const edgePush = (container: HTMLElement, pointer: PointerSpot) => {
  const box = container.getBoundingClientRect()
  return {
    x: pushAlong(pointer.x, box.left, box.right),
    y: pushAlong(pointer.y, box.top, box.bottom),
  }
}

interface EdgeScrollOptions {
  active: boolean
  scrollElement: () => HTMLElement | null
  pointer: MutableRefObject<PointerSpot>
  onScrolled: () => void
}

// Creeps the pane under a held pointer while it sits near an edge, so a drag can reach pages
// that are not on screen yet.
export const useEdgeScroll = ({
  active,
  scrollElement,
  pointer,
  onScrolled,
}: EdgeScrollOptions) => {
  useEffect(() => {
    if (!active) return

    let frame = 0
    const creep = () => {
      const container = scrollElement()
      const push = container ? edgePush(container, pointer.current) : null
      if (container && push && (push.x !== 0 || push.y !== 0)) {
        container.scrollLeft += push.x
        container.scrollTop += push.y
        onScrolled()
      }
      frame = requestAnimationFrame(creep)
    }

    frame = requestAnimationFrame(creep)
    return () => cancelAnimationFrame(frame)
  }, [active, scrollElement, pointer, onScrolled])
}
